import { StateCreator } from 'zustand';
import { Store } from 'store';
import { toggleSetArrayValue, toggleSetValue } from 'lib/utils';
import { DEFAULT_ROOM_COUNT_ARRAY } from '../../lib/constants';
import { PolygonSchema } from 'api/types';

export interface BuyApartmentSliceSecondary {
  selectedHouseIdsSecondary: Set<number>;
  setSelectedHouseIdsSecondary: (id: number[]) => void;

  selectedFullListCount: Set<number>;
  setSelectedFullListCount: (id: number[]) => void;

  priceRangeMin: number | undefined;
  setPriceRangeMin: (r: number | undefined) => void;

  priceRangePerSqmMax: number | undefined;
  setPriceRangePerSqmMax: (r: number | undefined) => void;

  priceRangePerSqmMin: number | undefined;
  setPriceRangePerSqmMin: (r: number | undefined) => void;

  priceRangeMax: number | undefined;
  setPriceRangeMax: (r: number | undefined) => void;

  areaSqFootageRangeMin: number | undefined;
  setAreaSqFootageRangeMin: (r: number | undefined) => void;

  secondaryDifferenceWithMarketPriceMin: number | undefined;
  setSecondaryDifferenceWithMarketPriceMin: (r: number | undefined) => void;

  secondaryDifferenceWithMarketPriceMax: number | undefined;
  setSecondaryDifferenceWithMarketPriceMax: (r: number | undefined) => void;

  areaSqFootageRangeMax: number | undefined;
  setAreaSqFootageRangeMax: (r: number | undefined) => void;

  selectedApartmentRoomSecondary: Set<number>;
  toggleSelectedApartmentRoomSecondary: (id: number) => void;
  clearApartmentRoomSecondary: () => void;

  selectedPolygonSecondary: PolygonSchema[] | null;
  setSelectedPolygonSecondary: (value: PolygonSchema[] | null) => void;

  selectedApartmentRoomTypes: Set<number>;
  toggleSelectedApartmentRoomTypes: (id: number | number[]) => void;
  clearApartmentRoomTypes: () => void;

  clearApartmentSliceSecondary: () => void;

  setFiltersSecondary: (
    filters: Partial<
      Pick<
        BuyApartmentSliceSecondary,
        | 'secondaryDifferenceWithMarketPriceMax'
        | 'secondaryDifferenceWithMarketPriceMin'
        | 'priceRangePerSqmMin'
        | 'priceRangePerSqmMax'
        | 'priceRangeMax'
        | 'priceRangeMin'
        | 'areaSqFootageRangeMin'
        | 'areaSqFootageRangeMax'
        | 'selectedApartmentRoomSecondary'
      >
    >,
  ) => void;
}

const createBuyApartmentSliceSecondary: StateCreator<
  Store,
  [['zustand/immer', never]],
  [],
  BuyApartmentSliceSecondary
> = (set) => ({
  selectedHouseIdsSecondary: new Set(),
  setSelectedHouseIdsSecondary: (ids) => {
    set((s) => toggleSetArrayValue(s.selectedHouseIdsSecondary, ids));
  },

  selectedFullListCount: new Set(DEFAULT_ROOM_COUNT_ARRAY),
  setSelectedFullListCount: (ids) => {
    set((s) => toggleSetArrayValue(s.selectedFullListCount, ids));
  },

  priceRangeMin: undefined,
  setPriceRangeMin: (value) => set({ priceRangeMin: value }),

  priceRangeMax: undefined,
  setPriceRangeMax: (value) => set({ priceRangeMax: value }),

  priceRangePerSqmMin: undefined,
  setPriceRangePerSqmMin: (value) => set({ priceRangePerSqmMin: value }),

  priceRangePerSqmMax: undefined,
  setPriceRangePerSqmMax: (value) => set({ priceRangePerSqmMax: value }),

  secondaryDifferenceWithMarketPriceMin: undefined,
  setSecondaryDifferenceWithMarketPriceMin: (value) =>
    set({ secondaryDifferenceWithMarketPriceMin: value }),

  secondaryDifferenceWithMarketPriceMax: undefined,
  setSecondaryDifferenceWithMarketPriceMax: (value) =>
    set({ secondaryDifferenceWithMarketPriceMax: value }),

  areaSqFootageRangeMin: undefined,
  setAreaSqFootageRangeMin: (value) => set({ areaSqFootageRangeMin: value }),

  areaSqFootageRangeMax: undefined,
  setAreaSqFootageRangeMax: (value) => set({ areaSqFootageRangeMax: value }),

  selectedApartmentRoomSecondary: new Set(),
  toggleSelectedApartmentRoomSecondary: (id) => {
    set((s) => toggleSetValue(s.selectedApartmentRoomSecondary, id));
  },

  clearApartmentRoomSecondary: () => set({ selectedApartmentRoomSecondary: new Set() }),

  selectedApartmentRoomTypes: new Set(),
  toggleSelectedApartmentRoomTypes: (id) => {
    set((s) => toggleSetValue(s.selectedApartmentRoomTypes, id));
  },
  clearApartmentRoomTypes: () => set({ selectedApartmentRoomTypes: new Set() }),

  selectedPolygonSecondary: null,
  setSelectedPolygonSecondary: (value: PolygonSchema[] | null) =>
    set({ selectedPolygonSecondary: value }),

  clearApartmentSliceSecondary: () =>
    set({
      selectedApartmentRoomSecondary: new Set(),
      selectedApartmentRoomTypes: new Set(),
      secondaryDifferenceWithMarketPriceMin: undefined,
      secondaryDifferenceWithMarketPriceMax: undefined,
      areaSqFootageRangeMax: undefined,
      areaSqFootageRangeMin: undefined,
      priceRangePerSqmMax: undefined,
      priceRangePerSqmMin: undefined,
      priceRangeMax: undefined,
      priceRangeMin: undefined,
      selectedHouseIdsSecondary: new Set(),
      selectedFullListCount: new Set(DEFAULT_ROOM_COUNT_ARRAY),
    }),

  setFiltersSecondary: (filters) => set((s) => void Object.assign(s, filters)),
});

export default createBuyApartmentSliceSecondary;
